import React, { useState, useRef, useEffect } from 'react';
import { motion as m, AnimatePresence as Ap } from 'framer-motion';
import { X, Send, Bot, Loader2, Minimize2 } from 'lucide-react';
import { useJarvis } from './JarvisProvider';
import { JarvisMessage } from './JarvisMessage';
import { theme } from '../../../lib/theme';
import { useTranslations } from '../../../i18n';

const getDateLabel = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Šodien';
    if (date.toDateString() === yesterday.toDateString()) return 'Vakar';
    
    return date.toLocaleDateString('lv-LV', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
    }).toUpperCase();
};

export function JarvisChat() {
    const { isOpen, toggleChat, messages, sendMessage, isLoading, markAsRead, loadMore, hasMore, totalCount, isFetchingHistory, currentStatus } = useJarvis();
    const [input, setInput] = useState('');
    const [firstUnreadId, setFirstUnreadId] = useState<string | null>(null);
    const { t } = useTranslations();
    const scrollRef = useRef<HTMLDivElement>(null);
    const unreadRef = useRef<HTMLDivElement>(null);
    const lastScrollHeight = useRef<number>(0);

    // Track scroll height before prepending messages
    useEffect(() => {
        if (isFetchingHistory && scrollRef.current) {
            lastScrollHeight.current = scrollRef.current.scrollHeight;
        }
    }, [isFetchingHistory]);

    // Track first unread when opening
    useEffect(() => {
        if (isOpen) {
            const firstUnread = messages.find(m => !m.is_read && m.role === 'assistant');
            if (firstUnread) {
                setFirstUnreadId(firstUnread.id);
            }
            // Mark all as read now that we've captured the first unread ID for visual purposes
            markAsRead();
        } else {
            setFirstUnreadId(null);
        }
    }, [isOpen]); // Removed markAsRead from deps to avoid re-triggering if messages change within this effect

    // Enhanced Scroll Logic
    useEffect(() => {
        if (!isOpen || !scrollRef.current) return;

        const performScroll = (instant = false) => {
            if (!scrollRef.current) return;

            if (unreadRef.current) {
                // Scroll to unread line
                unreadRef.current.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'start' });
            } else {
                // Scroll to bottom
                if (instant) {
                    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                } else {
                    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
                }
            }
        };

        // If we just finished fetching history, maintain scroll position
        if (!isFetchingHistory && lastScrollHeight.current > 0) {
            const newHeight = scrollRef.current.scrollHeight;
            const diff = newHeight - lastScrollHeight.current;
            scrollRef.current.scrollTop = diff;
            lastScrollHeight.current = 0;
            return;
        }

        // Only scroll to bottom for new messages or initial open
        if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1];
            // If it's a new message from user or assistant, or we are loading (waiting for response)
            if (lastMsg.role === 'user' || isLoading) {
                // Use instant scroll during loading/streaming to prevent jitter
                requestAnimationFrame(() => performScroll(isLoading));
            } else if (!isFetchingHistory) {
                // Initial load scroll or opening - give it a frame to calculate height
                requestAnimationFrame(() => performScroll(true));
                // And another one for safety (DOM sometimes slow)
                setTimeout(() => performScroll(true), 50);
            }
        }
    }, [messages, isLoading, isOpen, firstUnreadId, isFetchingHistory]);

    const handleSend = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!input.trim() || isLoading) return;
        
        const content = input;
        setInput('');
        await sendMessage(content);
    };

    return (
        <Ap>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <m.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100]"
                    />

                    {/* Centering Wrapper */}
                    <div className="fixed inset-0 z-[101] flex items-center justify-center p-0 md:p-6 pointer-events-none">
                        {/* Chat Modal */}
                        <m.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="pointer-events-auto w-full h-full md:h-[750px] md:w-[800px] md:max-h-[90vh] flex flex-col bg-white dark:bg-[#000D19] shadow-2xl md:rounded-[2.5rem] overflow-hidden border border-white/10"
                        >
                            {/* Header */}
                            <div className="px-6 py-5 border-b border-gray-100 dark:border-white/10 flex items-center justify-between bg-white/80 dark:bg-slate-900/40 backdrop-blur-md">
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 ${theme.colors.primaryLight} rounded-2xl flex items-center justify-center shadow-inner`}>
                                        <Bot size={28} className="text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Jarvis</h2>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest">{t.jarvis.subtitle}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={toggleChat}
                                        className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-full transition-colors text-gray-400 group"
                                        title="Minimize"
                                    >
                                        <Minimize2 size={22} className="group-hover:scale-110 transition-transform" />
                                    </button>
                                    <button 
                                        onClick={toggleChat}
                                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors text-gray-400 hover:text-red-500 md:hidden"
                                    >
                                        <X size={24} />
                                    </button>
                                </div>
                            </div>

                            {/* Messages Area */}
                            <div 
                                ref={scrollRef}
                                className="flex-1 overflow-y-auto p-6 md:p-8 space-y-2"
                            >
                                {messages.length === 0 && (
                                    <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-40">
                                        <Bot size={48} className="mb-4 text-blue-500" />
                                        <p className="text-sm font-medium">Hello! I am Jarvis.<br/>How can I help with your car today?</p>
                                    </div>
                                )}
                                
                                {hasMore && messages.length > 0 && (
                                    <div className="flex justify-center pb-6">
                                        <button 
                                            onClick={loadMore}
                                            disabled={isFetchingHistory}
                                            className="px-6 py-2 rounded-full border border-gray-200 dark:border-white/10 text-[11px] font-black uppercase tracking-widest text-gray-400 hover:text-blue-500 hover:border-blue-500/30 transition-all flex items-center gap-2 bg-white/50 dark:bg-white/5 backdrop-blur-sm"
                                        >
                                            {isFetchingHistory ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                                <Bot size={14} />
                                            )}
                                            {isFetchingHistory 
                                                ? (t.common?.loading || 'Ielādē...') 
                                                : `${t.jarvis?.loadOlder || 'Ielādēt vecākas ziņas'} (${Math.max(0, totalCount - messages.length)})`
                                            }
                                        </button>
                                    </div>
                                )}
                                {messages.map((msg, index) => {
                                    const isFirstUnread = msg.id === firstUnreadId;
                                    const prevMsg = messages[index - 1];
                                    const showDateSeparator = !prevMsg || new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();

                                    return (
                                        <React.Fragment key={msg.id}>
                                            {showDateSeparator && (
                                                <div className="flex items-center gap-4 py-10 px-4 opacity-30">
                                                    <div className="h-px bg-gray-400 flex-1" />
                                                    <span className="text-[9px] font-black uppercase tracking-widest whitespace-nowrap">{getDateLabel(msg.created_at)}</span>
                                                    <div className="h-px bg-gray-400 flex-1" />
                                                </div>
                                            )}
                                            
                                            {isFirstUnread && (
                                                <div ref={unreadRef} className="flex items-center gap-4 py-6">
                                                    <div className="flex-1 h-[1.5px] bg-red-500/40" />
                                                    <div className="px-3 py-1 bg-red-500/10 rounded-full border border-red-500/20">
                                                        <span className="text-[10px] font-black text-red-500 uppercase tracking-[0.2em] whitespace-nowrap">Jaunas ziņas</span>
                                                    </div>
                                                    <div className="flex-1 h-[1.5px] bg-red-500/40" />
                                                </div>
                                            )}
                                            <JarvisMessage message={msg} />
                                        </React.Fragment>
                                    );
                                })}
                                {isLoading && (
                                    <div className="flex justify-start mb-4">
                                        <div className={`${theme.colors.cardBg} ${theme.colors.border} border rounded-2xl rounded-tl-none px-5 py-3 shadow-sm flex items-center gap-3`}>
                                            <Loader2 size={18} className="animate-spin text-blue-600 dark:text-blue-400" />
                                            <span className="text-sm font-bold text-gray-500 dark:text-gray-400 tracking-tight">
                                                {currentStatus || t.jarvis.typing}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Input Area */}
                            <div className="p-6 bg-gray-50/50 dark:bg-slate-900/60 border-t border-gray-100 dark:border-white/10">
                                <form 
                                    onSubmit={handleSend}
                                    className="relative flex items-center gap-3"
                                >
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onFocus={markAsRead}
                                        placeholder={t.jarvis.askJarvis}
                                        disabled={isLoading}
                                        autoFocus
                                        className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[1.5rem] px-6 py-4 pr-14 text-[16px] focus:outline-none focus:ring-4 focus:ring-blue-500/10 dark:text-white transition-all shadow-sm"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!input.trim() || isLoading}
                                        className={`absolute right-2 p-3 rounded-2xl transition-all ${
                                            input.trim() && !isLoading 
                                                ? `${theme.colors.primaryBg} text-white shadow-xl scale-110` 
                                                : 'text-gray-300 dark:text-gray-600'
                                        }`}
                                    >
                                        <Send size={22} />
                                    </button>
                                </form>
                            </div>
                        </m.div>
                    </div>
                </>
            )}
        </Ap>
    );
}
